import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Services from "@/components/Services";
import WhyChooseUs from "@/components/WhyChooseUs";
import Testimonials from "@/components/Testimonials";
import Team from "@/components/Team";
import BookingForm from "@/components/BookingForm";
import ContactForm from "@/components/ContactForm";
import Footer from "@/components/Footer";
import ServiceDetail from "@/components/ServiceDetail";
import AboutUs from "@/components/AboutUs";
import OurTeam from "@/components/OurTeam";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [team, setTeam] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [testimonialsRes, teamRes, servicesRes] = await Promise.all([
          axios.get(`${API}/testimonials`),
          axios.get(`${API}/team`),
          axios.get(`${API}/services`)
        ]);
        setTestimonials(testimonialsRes.data);
        setTeam(teamRes.data);
        setServices(servicesRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load some data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleBookingSubmit = async (data) => {
    try {
      await axios.post(`${API}/bookings`, data);
      toast.success("Booking request submitted! We'll contact you soon.");
      return true;
    } catch (error) {
      console.error("Booking error:", error);
      toast.error("Failed to submit booking. Please try again.");
      return false;
    }
  };

  const handleContactSubmit = async (data) => {
    try {
      await axios.post(`${API}/contact`, data);
      toast.success("Message sent! We'll get back to you shortly.");
      return true;
    } catch (error) {
      console.error("Contact error:", error);
      toast.error("Failed to send message. Please try again.");
      return false;
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <Navbar />
      <main>
        <Hero />
        <Services services={services} loading={loading} />
        <WhyChooseUs />
        <Testimonials testimonials={testimonials} loading={loading} />
        <section id="booking" className="py-16 md:py-24 bg-gradient-to-b from-white to-sky-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
              <div>
                <span className="text-[#00A3BF] font-medium text-sm uppercase tracking-wider">Get Started</span>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-2 mb-4">
                  Book Your Cleaning
                </h2>
                <p className="text-slate-600 text-lg mb-8">
                  Fill out the form and we'll get back to you within 24 hours with a personalized quote.
                </p>
                <BookingForm onSubmit={handleBookingSubmit} services={services} />
              </div>
              <div>
                <Team team={team} loading={loading} />
              </div>
            </div>
          </div>
        </section>
        <ContactForm onSubmit={handleContactSubmit} />
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/services/:serviceId" element={<ServiceDetail />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/team" element={<OurTeam />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
